/**
 * A marca do OmieChat.
 *
 * Desenhada aqui pelo mesmo motivo dos ícones (ver `icons.tsx`): nada de dependência nova, e a marca
 * escala de 16px (favicon) a 64px (tela de entrada) sem virar borrão — o que uma imagem raster não
 * faria.
 *
 * A leitura do desenho, para quem for mexer nele depois:
 *
 * - **balão de conversa** com o fundo estrelado. O produto é um webchat, e o balão é o que diz isso
 *   em 16px, onde nenhum detalhe interno sobrevive;
 * - **o anel branco no meio é o "O" de Omie.** É a única parte que precisa continuar legível quando
 *   tudo mais desaparece, e é por isso que ela é a forma de maior contraste do desenho — branco puro
 *   sobre o azul mais escuro;
 * - **a estrela** e as fagulhas douradas dão o "algo a mais" sem competir com o anel: ficam na borda,
 *   em tamanho pequeno, e podem sumir no favicon sem prejuízo.
 *
 * As estrelas do fundo são recortadas pelo balão (`clipPath`), senão sobrariam pontos brancos soltos
 * no ar em volta dele.
 *
 * O gradiente usa `gradientUnits="userSpaceOnUse"` de propósito: o balão é **duas** formas (o corpo e
 * a ponta), e com o padrão `objectBoundingBox` cada uma receberia o gradiente na sua própria caixa —
 * a ponta sairia com uma faixa de cor diferente e a emenda apareceria.
 */

/** Um id por instância não é necessário: o `clipPath` e o gradiente são idênticos em toda instância. */
const GRADIENTE = 'omiechat-galaxia';
const RECORTE = 'omiechat-balao';

/** Fagulhas do fundo: posição e raio fixos, para o desenho ser sempre o mesmo. */
const ESTRELAS: [number, number, number, number][] = [
  // x, y, raio, opacidade
  [22, 16, 0.9, 0.9],
  [41, 14, 0.7, 0.75],
  [46, 24, 1.1, 0.85],
  [17, 28, 0.8, 0.7],
  [25, 40, 1, 0.8],
  [43, 39, 0.75, 0.65],
  [33, 12, 0.6, 0.6],
  [15, 38, 0.6, 0.55],
  [49, 33, 0.6, 0.5],
];

export interface LogoProps {
  /** Lado do quadrado, em px. */
  size?: number;
  /**
   * Desenha o fundo branco arredondado do ícone de aplicativo.
   *
   * Ligado no favicon e na tela de entrada, onde a marca aparece sozinha. Desligado no cabeçalho do
   * painel, que já é branco — ali o quadrado só acrescentaria uma moldura sem função.
   */
  framed?: boolean;
  className?: string;
}

export function Logo({ size = 32, framed = false, className = '' }: LogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      // Decorativo: onde a marca aparece, o nome "OmieChat" aparece escrito ao lado.
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <defs>
        <linearGradient id={GRADIENTE} x1="12" y1="8" x2="52" y2="52" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#3d6ff5" />
          <stop offset="0.45" stopColor="#1b3fb8" />
          <stop offset="1" stopColor="#0b1c66" />
        </linearGradient>
        <clipPath id={RECORTE}>
          <circle cx="32" cy="29" r="24" />
        </clipPath>
      </defs>

      {framed ? <rect width="64" height="64" rx="14" fill="#ffffff" /> : null}

      {/* Ponta do balão, desenhada antes do corpo para a emenda ficar coberta por ele. */}
      <path d="M21 45 L9.5 57 L14.5 42 Z" fill={`url(#${GRADIENTE})`} />
      <circle cx="32" cy="29" r="24" fill={`url(#${GRADIENTE})`} />

      <g clipPath={`url(#${RECORTE})`}>
        {ESTRELAS.map(([x, y, r, o]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill="#ffffff" opacity={o} />
        ))}
      </g>

      {/* O "O" de Omie: anel branco com o miolo no azul mais escuro do gradiente. */}
      <circle cx="32" cy="29" r="14.5" fill="#ffffff" />
      <circle cx="32" cy="29" r="7" fill="#12307f" />

      {/* Estrela de quatro pontas, no canto onde não briga com o anel. */}
      <path
        d="M50.5 6 L52.7 12.3 L59 14.5 L52.7 16.7 L50.5 23 L48.3 16.7 L42 14.5 L48.3 12.3 Z"
        fill="#ffffff"
        stroke="#12307f"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />

      {/* Fagulhas douradas: o "algo a mais", pequenas o bastante para sumirem no favicon. */}
      <g fill="#e2b970">
        <path d="M12 20 L12.8 22 L14.8 22.8 L12.8 23.6 L12 25.6 L11.2 23.6 L9.2 22.8 L11.2 22 Z" />
        <path d="M54 40 L54.7 41.8 L56.5 42.5 L54.7 43.2 L54 45 L53.3 43.2 L51.5 42.5 L53.3 41.8 Z" />
        <circle cx="20" cy="51" r="1.3" />
      </g>
    </svg>
  );
}

/**
 * Marca + nome, que é como a identidade aparece na maioria dos lugares.
 *
 * O nome vai como texto e não dentro do SVG: assim ele é selecionável, encontrável pelo Ctrl+F e
 * herda a fonte do painel — três coisas que um logotipo vetorizado perde.
 */
export function Wordmark({ size = 28, className = '' }: { size?: number; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <Logo size={size} />
      <span className="text-base font-semibold tracking-tight text-slate-900">OmieChat</span>
    </span>
  );
}
